/**
 * Uploads one image to R2 through the Pages Function at
 * `functions/api/upload-image.ts`, and returns the public URL to store.
 *
 * **Why an endpoint and not a direct write.** R2 has no anonymous write, and a
 * signed one needs the account secret. This is a Vite SPA, so anything it holds
 * ships in a public bundle — the same hazard `.env.example` documents for the
 * deploy hooks, except a bucket key is not a rebuild but a write to storage. So
 * the browser sends the file and its Firebase ID token, and the function
 * decides. See the header of the function for the bindings it needs.
 *
 * **`XMLHttpRequest`, not `fetch`.** `fetch` reports no upload progress —
 * request streaming is not available in Safari, and a 10 MB image over a slow
 * connection with no feedback reads as a hung form. XHR's `upload.onprogress`
 * is the only portable way to drive a real progress indicator.
 */

/** The endpoint's own error codes, plus the two this module raises itself. */
export type UploadErrorCode =
    | "not_configured"
    | "network"
    | "server_misconfigured"
    | "missing_token"
    | "not_a_one_ai_account"
    | "invalid_form_data"
    | "missing_file"
    | "not_an_image"
    | "too_large"
    | "upload_failed"
    | "unknown";

export class UploadError extends Error {
    constructor(readonly code: UploadErrorCode) {
        super(code);
        this.name = "UploadError";
    }
}

/** Mirrors `MAX_BYTES` in the function, so the check can happen before the POST. */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export interface UploadImageOptions {
    file: File;
    /** A Firebase ID token, from `authController.getAuthToken()`. */
    token: string;
    /** 0–100, called as the bytes go out. */
    onProgress?: (percent: number) => void;
}

export function uploadImage({
    file,
    token,
    onProgress,
}: UploadImageOptions): Promise<string> {
    const endpoint = import.meta.env.VITE_UPLOAD_ENDPOINT;
    // Unset is the normal state of a fresh `.env`, so it gets its own code and
    // a message that names the variable, rather than a network error at a
    // relative URL that would resolve against the CMS's own origin.
    if (!endpoint) return Promise.reject(new UploadError("not_configured"));

    const form = new FormData();
    form.append("file", file);

    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open("POST", endpoint);
        request.setRequestHeader("Authorization", `Bearer ${token}`);
        // Deliberately no content-type: the browser has to set the multipart
        // boundary itself, and setting it by hand produces a body the function
        // cannot parse.

        request.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                onProgress?.(Math.round((event.loaded / event.total) * 100));
            }
        };

        request.onerror = () => reject(new UploadError("network"));
        request.ontimeout = () => reject(new UploadError("network"));

        request.onload = () => {
            let body: { url?: string; error?: string } = {};
            try {
                body = JSON.parse(request.responseText) as typeof body;
            } catch {
                // A non-JSON body means something other than the function
                // answered — a Cloudflare error page, most likely.
            }

            if (request.status === 200 && body.url) {
                resolve(body.url);
                return;
            }
            reject(new UploadError((body.error as UploadErrorCode) ?? "unknown"));
        };

        request.send(form);
    });
}
